import { useRef, useState } from 'react'

import { FvButton, OrDivider, ValidatedForm } from '@fv/client-components'
import { submitForm } from '@fv/client-core'

import { FooterBar } from '@/components/FooterBar'
import {
  ShipperSlider,
  type ShipperSliderProps,
} from '@/components/ShipperSlider'
import { useIsNmfcRequired } from '@/features/settings/account-settings/hooks'

import { useProductCatalog } from '../useProductCatalog'
import { ContainsRow } from './ContainsRow'
import { defaultLoadContainsItem, isValidCommodity } from './loadItemHelpers'
import { useItemActions, useItemsState } from './loadItemsContext'
import { PanelItemSummary } from './PanelItemSummary'
import type {
  ItemChangeFn,
  LoadHandlingUnitFormModel,
  LoadItemFormModel,
} from './types'

const isLoadItemFormModel = (
  v: LoadItemFormModel | undefined,
): v is LoadItemFormModel => {
  return v !== undefined && !!v._id
}

type Props = Pick<ShipperSliderProps, 'onClose' | 'isOpen'> & {
  handlingUnit?: LoadHandlingUnitFormModel
  editingIx?: number
  onSubmit: (contains: Pick<LoadHandlingUnitFormModel, 'contains'>) => void
}

const HandlingUnitPanelContents = ({
  handlingUnit,
  onSubmit,
  onClose,
}: Props) => {
  const formRef = useRef<HTMLFormElement>(null)
  const { workflow, activeContainsIndex, step, isTemplate } = useItemsState()
  const { activateContains } = useItemActions()
  const requireNmfc = useIsNmfcRequired(workflow)
  const [contains, setContains] = useState(
    handlingUnit?.contains?.filter(isLoadItemFormModel) ?? [],
  )
  const handleRemove = (index: number) => {
    setContains(prev => prev.filter((i, ix) => ix !== index))
  }
  const verifyFormValidity = (): boolean => {
    const isValid = formRef.current && formRef.current.checkValidity()
    if (!isValid) {
      submitForm(formRef.current)
    } else {
      formRef.current.reset()
    }
    return !!isValid
  }
  const handleAddClick = () => {
    if (!verifyFormValidity()) {
      return
    }
    setContains([...contains, defaultLoadContainsItem()])
    activateContains(contains.length)
  }
  const handleEditClick = (index: number) => {
    if (!verifyFormValidity()) {
      return
    }
    activateContains(index)
  }
  const handleChange =
    (index: number): ItemChangeFn =>
    value => {
      setContains(prev =>
        prev.map((p, ix) =>
          ix !== index
            ? p
            : {
                ...p,
                ...value,
              },
        ),
      )
    }

  const handleSubmit = (form: HTMLFormElement) => {
    let isValid = true
    contains.forEach((c, ix) => {
      if (!isTemplate && !isValidCommodity(c, step, requireNmfc)) {
        activateContains(ix)
        isValid = false
      }
    })
    if (isValid) {
      onSubmit({ contains })
    } else {
      form.checkValidity()
      submitForm(form)
    }
  }

  if (!handlingUnit) return null

  return (
    <ValidatedForm
      onValidSubmit={handleSubmit}
      ref={formRef}
      className="flex flex-1 flex-col overflow-hidden"
    >
      <div className="overflow-auto flex-1">
        <div className="blue-container-light !border-x-0 !border-t-0 px-6 py-4">
          <PanelItemSummary item={handlingUnit} workflow={workflow} />
        </div>
        <div>
          {contains.map((item, index) => (
            <ContainsRow
              key={index}
              index={index + 1}
              value={item}
              editing={activeContainsIndex === index}
              onRemoveClick={() => handleRemove(index)}
              onEditClick={() => handleEditClick(index)}
              onChange={handleChange(index)}
            />
          ))}
          <div className="p-2">
            <FvButton theme="plain" icon="plus" onClick={handleAddClick}>
              Add commodity
            </FvButton>
          </div>
        </div>
      </div>
      <FooterBar className="gap-x-4">
        <FvButton theme="primary" fwd icon="check" type="submit">
          Finish and save
        </FvButton>
        <OrDivider />
        <FvButton icon="times" onClick={onClose}>
          Cancel
        </FvButton>
      </FooterBar>
    </ValidatedForm>
  )
}
export const HandlingUnitContainsPanel = (props: Props) => {
  const closeProductCatalog = useProductCatalog(s => s.close)
  const handleClose = () => {
    closeProductCatalog()
    props.onClose?.()
  }
  return (
    <ShipperSlider isOpen={props.isOpen} onClose={handleClose} flush>
      <HandlingUnitPanelContents {...props} />
    </ShipperSlider>
  )
}
